<template>
  <div class="wrapper_group_dashboard">
    <div class="group_dashboard">
      <div class="all_invoices">
        <breadcrumb :items="breadcrumbItems" class="invoice-detail-breadcrumb" />
        <div class="title">
          <h2>Group Invoices</h2>
          <div class="date_paynow">
            <date-picker
              v-model="selectedMonth"
              @input="changeMonth"
              type="month"
              :clearable="false"
              placeholder="Select Month"
            ></date-picker>
          </div>
        </div>

        <p v-if="errorMessage" class="text-danger mg_message">{{ errorMessage }}</p>

        <template v-else-if="loaded">
          <p v-if="summary.group_info" class="mg_banner">
            Viewing invoices of downline groups under
            <strong>{{ summary.group_info.name }}</strong>
            for {{ monthLabel }}
          </p>

          <div v-if="summary.invoice" class="mg_summary">
            <div class="mg_stat">
              <span class="mg_stat_label">Total Groups</span>
              <span class="mg_stat_value">{{ summary.invoice.member_group_count }}</span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Invoice Total</span>
              <span class="mg_stat_value">{{ summary.invoice.invoice_total | currency }}</span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Enrollment Fee</span>
              <span class="mg_stat_value">{{ summary.invoice.invoice_efee | currency }}</span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Previous Due</span>
              <span class="mg_stat_value">{{ summary.invoice.invoice_prev_due | currency }}</span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Paid Amount</span>
              <span class="mg_stat_value">{{ summary.invoice.invoice_due_amount_paid | currency }}</span>
            </div>
            <div class="mg_stat mg_stat_due">
              <span class="mg_stat_label">Due Amount</span>
              <span class="mg_stat_value">{{ summary.invoice.invoice_due_amount | currency }}</span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Status</span>
              <span :class="['mg_status', statusClass(summary.invoice.invoice_payment_status)]">
                {{ summary.invoice.invoice_payment_status }}
              </span>
            </div>
          </div>

          <no-content v-if="!summary.invoice" text="No multi-group invoice for this period." />

          <template v-else>
            <div class="mg_filters">
              <select v-model="statusFilter" class="form-control">
                <option v-for="status in statusOptions" :key="status" :value="status">{{ status }}</option>
              </select>
              <input v-model="search" type="text" class="form-control" placeholder="Search" />
            </div>

            <div class="table_container" style="overflow-x: auto">
              <table>
                <thead>
                  <tr>
                    <td>Inv #</td>
                    <td>Group</td>
                    <td>Dates</td>
                    <td>Totals</td>
                    <td>Status</td>
                    <td>Action</td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in filteredRecords" :key="item.group_invoice_id">
                    <td>
                      <strong>{{ item.invoice_number }}</strong>
                    </td>
                    <td>
                      {{ item.group_name }}
                      <br />
                      #{{ item.invoice_group_id }}
                      <br />
                      {{ item.group_city }}, {{ item.group_state }} {{ item.group_zip }}
                    </td>
                    <td>
                      Invoice: {{ item.invoice_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                      <br />
                      Due: {{ item.invoice_due_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                      <br />
                      Period:
                      {{ item.invoice_start_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                      -
                      {{ item.invoice_end_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                    </td>
                    <td>
                      Total: {{ item.invoice_total | currency }}
                      <br />
                      Enrollment Fee: {{ item.invoice_efee | currency }}
                      <span v-if="item.invoice_late_fee > 0">
                        <br />
                        Late Fee: {{ item.invoice_late_fee | currency }}
                      </span>
                      <span v-if="item.invoice_bounce_fee > 0">
                        <br />
                        Bounce Fee: {{ item.invoice_bounce_fee | currency }}
                      </span>
                      <span v-if="item.invoice_stmt_fee > 0">
                        <br />
                        Statement Fee: {{ item.invoice_stmt_fee | currency }}
                      </span>
                      <br />
                      Previous Due: {{ item.invoice_prev_due | currency }}
                      <br />
                      Due: {{ item.invoice_due_amount | currency }}
                      <br />
                      Paid: {{ item.invoice_due_amount_paid | currency }}
                    </td>
                    <td>
                      {{ item.invoice_payment_status }}
                      <span v-if="item.payment_party_status === 'PROCESSING'">
                        <br />
                        Processing
                        <span v-if="item.processing_date">
                          ({{ item.processing_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }})
                        </span>
                      </span>
                    </td>
                    <td>
                      <div class="icons">
                        <a
                          v-if="item.invoice_end_date"
                          href="javascript:;"
                          class="action_container"
                          v-tooltip="'View Details'"
                          @click="openInvoiceDetails(item)"
                        >
                          <span class="action_icon">
                            <i class="fas fa-info"></i>
                          </span>
                          View Details
                        </a>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <no-content v-if="!filteredRecords.length" text="No group invoices found." />
            </div>
            <pagination v-if="rows.records" :rows.sync="rows" :filters="{}" />
          </template>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import Api from "@/resource/Api";
import moment from "moment";

const FORBIDDEN_MESSAGE = "You are not authorized to view multi-group invoices.";

export default {
  name: "MultiGroupGroupInvoices",
  data() {
    return {
      breadcrumbItems: [{ title: "Group Invoices", active: true }],
      selectedMonth: new Date(),
      summary: { group_info: null, downline_group_count: null, invoice: null },
      rows: {},
      loaded: false,
      errorMessage: "",
      statusFilter: "ALL",
      statusOptions: ["ALL", "UNPAID", "PAID", "PARTIAL"],
      search: "",
    };
  },
  computed: {
    invoiceDate() {
      return this.$route.params.date;
    },
    monthLabel() {
      return moment(this.invoiceDate, "YYYY-MM-DD").format("MMMM YYYY");
    },
    filteredRecords() {
      const records = this.rows.records || [];
      const search = this.search.trim().toLowerCase();
      return records.filter((item) => {
        if (this.statusFilter !== "ALL" && item.invoice_payment_status !== this.statusFilter) {
          return false;
        }
        if (!search) {
          return true;
        }
        return [
          item.invoice_number,
          item.invoice_group_id,
          item.group_name,
          item.group_city,
          item.group_state,
          item.group_zip,
        ].some((value) => value !== null && value !== undefined && String(value).toLowerCase().includes(search));
      });
    },
  },
  watch: {
    "$route.params.date"() {
      this.load();
    },
  },
  methods: {
    statusClass(status) {
      if (status === "PAID") return "mg_status_paid";
      if (status === "PARTIAL") return "mg_status_partial";
      return "mg_status_unpaid";
    },
    openInvoiceDetails(item) {
      const route = this.$router.resolve({
        name: "MultiGroupSubGroupInvoiceDetails",
        params: { subGroupId: item.invoice_group_id, date: item.invoice_end_date },
      });
      window.open(route.href, "_blank");
    },
    isValidDate(date) {
      return !!date && moment(date, "YYYY-MM-DD", true).isValid() && date.endsWith("-01");
    },
    changeMonth(month) {
      if (!month) {
        return;
      }
      const date = moment(month).startOf("month").format("YYYY-MM-DD");
      if (date !== this.invoiceDate) {
        this.$router.replace({ name: "MultiGroupGroupInvoices", params: { date } });
      }
    },
    handleError(err, fallback) {
      const forbidden = err && err.response && err.response.status === 403;
      this.errorMessage = forbidden ? FORBIDDEN_MESSAGE : fallback;
      if (!forbidden) {
        this.$notify(this.errorMessage, "error");
      }
    },
    load() {
      if (!this.isValidDate(this.invoiceDate)) {
        this.$router.replace({
          name: "MultiGroupGroupInvoices",
          params: { date: moment().startOf("month").format("YYYY-MM-DD") },
        });
        return;
      }

      this.selectedMonth = moment(this.invoiceDate, "YYYY-MM-DD").toDate();
      this.errorMessage = "";
      this.loaded = false;
      this.rows = {};
      let loader = this.$loading.show();

      Api.get(`multigroup-invoice/summary/${this.invoiceDate}`)
        .then((res) => {
          this.summary = res.data.data;
          if (!this.summary.invoice) {
            return null;
          }
          return Api.get(`multigroup-invoice/group-invoices/${this.invoiceDate}`).then((rowsRes) => {
            this.rows = rowsRes.data;
          });
        })
        .then(() => {
          this.loaded = true;
        })
        .catch((err) => {
          this.handleError(err, "Unable to load group invoices for this period. Please try again later.");
        })
        .finally(() => {
          loader.hide();
        });
    },
  },
  created() {
    this.load();
  },
};
</script>
<style src="@/assets/css/group_dashboard/all_invoices.css"></style>
<style scoped>
.mg_banner {
  margin-top: 15px;
  color: #254050;
}
.mg_summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 15px;
}
.mg_stat {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 10px;
  box-shadow: -2px 2px 5px #43566420;
}
.mg_stat_label {
  font-size: 12px;
  color: #778699;
}
.mg_stat_value {
  font-size: 18px;
  font-weight: 600;
  color: #254050;
}
.mg_stat_due {
  border-left: 3px solid #03c3ec;
}
.mg_stat_due .mg_stat_value {
  color: #0396b6;
}
.mg_status {
  align-self: flex-start;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}
.mg_status_paid {
  background: #e3f6e6;
  color: #2e8b3a;
}
.mg_status_partial {
  background: #fff3dc;
  color: #b07a12;
}
.mg_status_unpaid {
  background: #fde6e9;
  color: #d0283e;
}
.mg_message {
  margin-top: 20px;
}
.mg_filters {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  max-width: 500px;
}
</style>
